import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Module {
  id: string;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  route: string | null;
  component_name: string | null;
}

export interface Permission {
  id: string;
  name: string;
  code: string;
  description: string | null;
  module_id: string | null;
}

export interface UserRole {
  id: string;
  role_id: string;
  company_id: string;
  is_active: boolean;
  role: {
    id: string;
    name: string;
    code: string;
    description: string | null;
  };
}

export const usePermissions = () => {
  const { user, selectedCompany } = useAuth();
  const [modules, setModules] = useState<Module[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  // Función memoizada para verificar si el usuario es super admin (role-based only)
  const isSuperAdmin = useMemo(() => {
    const isSuper = userRoles.some(ur => ur.role.code === 'super_admin' && ur.is_active);
    return isSuper;
  }, [user?.id, userRoles]);

  // Función para verificar si el usuario tiene un permiso específico
  const hasPermission = useCallback((permissionCode: string): boolean => {
    // 🔒 SEGURIDAD CRÍTICA: SIN USUARIO = SIN PERMISOS
    if (!user?.id) {
      // Solo mostrar warning si ya terminó de cargar (no es carga inicial)
      if (!loading) {
        console.warn('🚨 SEGURIDAD: Acceso denegado - No hay usuario autenticado');
      }
      return false;
    }

    // 🚫 SUPER ADMIN BYPASS TOTAL: Super admins tienen acceso completo (role-based)
    if (isSuperAdmin) {
      console.log('🔐 [SUPER ADMIN] Bypass total activado para:', permissionCode, 'Usuario:', user.id);
      return true;
    }

    // 🔒 SEGURIDAD: Algunos permisos críticos requieren empresa seleccionada
    // ⚠️ NOTA: view_sales y manage_sales NO están aquí porque los cajeros necesitan
    // acceder al POS para seleccionar su sesión, que luego define la empresa/sucursal
    const requiresCompany = [
      'manage_inventory_global', // Solo permisos que realmente afectan múltiples empresas
      'view_reports_global',
      'manage_system_config'
    ];
    if (requiresCompany.includes(permissionCode) && !selectedCompany?.id) {
      console.warn('🚨 SEGURIDAD: Acceso denegado - Permiso requiere empresa seleccionada:', permissionCode);
      return false;
    }

    // Debug específico para manage_inventory
    if (permissionCode === 'manage_inventory') {
      console.log('🔐 [DEBUG] manage_inventory permission check:', {
        permissionCode,
        loading,
        isSuperAdmin,
        userId: user?.id,
        selectedCompany: selectedCompany?.name,
        userRoles: userRoles.map(ur => ({ code: ur.role.code, active: ur.is_active })),
        userPermissions: userPermissions.slice(0, 10), // Solo las primeras 10
        hasDirectPermission: userPermissions.includes(permissionCode)
      });
    }
    
    // Si está cargando Y no es super admin, denegar temporalmente
    if (loading) {
      return false;
    }
    
    // Para super admin que tiene '*', permitir todo
    if (userPermissions.includes('*')) {
      return true;
    }

    // Regla: manage_{x} implica view_{x} y access_{x}
    if (permissionCode.startsWith('view_')) {
      const suffix = permissionCode.slice(5);
      if (userPermissions.includes(`manage_${suffix}`)) {
        console.log(`✅ [hasPermission] ${permissionCode} concedido por manage_${suffix}`);
        return true;
      }
    }
    if (permissionCode.startsWith('access_')) {
      const suffix = permissionCode.slice(7);
      if (
        userPermissions.includes(`manage_${suffix}`) ||
        userPermissions.includes(`view_${suffix}`)
      ) {
        console.log(`✅ [hasPermission] ${permissionCode} concedido por view/manage ${suffix}`);
        return true;
      }
    }

    const result = userPermissions.includes(permissionCode);
    if (permissionCode === 'manage_sales' || permissionCode === 'view_sales') {
      console.log('🧪 [hasPermission] check', { permissionCode, result, userPermissions });
    }
    return result;
  }, [user?.id, isSuperAdmin, userPermissions, userRoles, selectedCompany, loading]);

  // Función para verificar si el usuario tiene un rol específico
  const hasRole = useCallback((roleCode: string): boolean => {
    return userRoles.some(ur => ur.role.code === roleCode && ur.is_active);
  }, [userRoles]);

  // Función memoizada para obtener módulos accesibles para el usuario
  const getAccessibleModules = useMemo((): Module[] => {
    return modules.filter(module => {
      // Si no hay un permiso específico para el módulo, permitir acceso
      if (!module.code) return true;
      
      // Verificar permisos básicos por módulo
      const modulePermissions = [
        `view_${module.code}`,
        `manage_${module.code}`,
        `access_${module.code}`
      ];
      
      return modulePermissions.some(permission => hasPermission(permission)) ||
             hasPermission('view_dashboard'); // Dashboard siempre accesible si tiene permisos básicos
    });
  }, [modules, hasPermission]);

  // Obtener módulos principales (sin parent_id)
  const getMainModules = useMemo((): Module[] => {
    return getAccessibleModules.filter(module => !module.parent_id);
  }, [getAccessibleModules]);

  // Obtener submódulos de un módulo específico
  const getSubModules = useCallback((parentId: string): Module[] => {
    return getAccessibleModules.filter(module => module.parent_id === parentId);
  }, [getAccessibleModules]);

  // Cargar módulos
  const loadModules = async () => {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  // Cargar permisos del usuario
  const loadUserPermissions = async () => {
    if (!user) {
      console.log('🔐 [Permissions] No user, skipping permission load');
      setUserPermissions([]);
      setUserRoles([]);
      return;
    }

    try {
      // Obtener TODOS los roles del usuario (sin filtrar por empresa)
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          *,
          role:roles (
            id,
            name,
            code,
            description
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (rolesError) throw rolesError;
      setUserRoles(rolesData || []);

      if (!rolesData || rolesData.length === 0) {
        setUserPermissions([]);
        return;
      }

      // Si usuario tiene rol super_admin => acceso total (role-based only)
      const hasSuper = rolesData.some(ur => ur.role?.code === 'super_admin' && ur.is_active);
      if (hasSuper) {
        setUserPermissions(['*']);
        return;
      }

      // Usar la función SECURITY DEFINER para obtener permisos (evita RLS en role_permissions)
      const { data: permissionsData, error: permissionsError } = await supabase
        .rpc('get_user_permission_codes', { _user_id: user.id });

      if (permissionsError) throw permissionsError;

      const permissions = permissionsData?.map((p: any) => p.permission_code) || [];
      setUserPermissions(permissions);
    } catch (error) {
      setUserPermissions([]);
      setUserRoles([]);
    }
  };

  // Efecto para cargar datos iniciales con debouncing
  useEffect(() => {
    // Debounce para evitar recargas excesivas
    const timeoutId = setTimeout(() => {
      const loadData = async () => {
        setLoading(true);
        try {
          await Promise.all([
            loadModules(),
            loadUserPermissions()
          ]);
        } catch (error) {
          console.error('Error loading permissions data:', error);
        } finally {
          setLoading(false);
        }
      };

      if (user?.id) {
        loadData();
      } else {
        setLoading(false);
        setUserPermissions([]);
        setUserRoles([]);
        setModules([]);
      }
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, [user?.id, selectedCompany?.id]);

  return {
    modules,
    userPermissions,
    userRoles,
    loading,
    hasPermission,
    hasRole,
    getAccessibleModules,
    getMainModules,
    getSubModules,
    loadModules,
    loadUserPermissions
  };
};